// src/services/api.js

// === BASE URL (HTTPS-safe y con sufijo /api garantizado) ===
const RAW = (import.meta.env?.VITE_API_URL || "http://localhost:8000").replace(/\/+$/, "");
const RAW_SECURE =
  typeof window !== "undefined" && window.location.protocol === "https:"
    ? RAW.replace(/^http:\/\//, "https://")
    : RAW;
// Fuerza sufijo /api en la base (evita /api/api más abajo)
export const API_URL = /\/api$/i.test(RAW_SECURE) ? RAW_SECURE : `${RAW_SECURE}/api`;

// === Cookies httpOnly para refresh ===
const USE_CREDENTIALS = true;

// ====== almacén de access token ======
const ACCESS_KEYS = ["access", "access_token", "token"];
const USER_KEYS = ["user", "profile"];
const REMEMBER_KEY = "auth_remember"; // "true" | "false"

const storage = {
  remember() {
    try {
      const flag =
        (typeof localStorage !== "undefined" && localStorage.getItem(REMEMBER_KEY)) ??
        (typeof sessionStorage !== "undefined" && sessionStorage.getItem(REMEMBER_KEY));
      if (flag === "true") return true;
      if (flag === "false") return false;
    } catch {}
    try {
      if (typeof localStorage !== "undefined") {
        for (const k of ACCESS_KEYS) if (localStorage.getItem(k)) return true;
      }
    } catch {}
    return false;
  },
  get() {
    let v = null;
    if (typeof sessionStorage !== "undefined") {
      for (const k of ACCESS_KEYS) { v ||= sessionStorage.getItem(k); if (v) break; }
    }
    if (!v && typeof localStorage !== "undefined") {
      for (const k of ACCESS_KEYS) { v ||= localStorage.getItem(k); if (v) break; }
    }
    return v;
  },
  set(token, remember = false) {
    if (!token) return this.clear();
    try {
      if (typeof sessionStorage !== "undefined") for (const k of ACCESS_KEYS) sessionStorage.removeItem(k);
      if (typeof localStorage !== "undefined")  for (const k of ACCESS_KEYS) localStorage.removeItem(k);
    } catch {}
    try {
      if (remember) {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("access", token);
          localStorage.setItem(REMEMBER_KEY, "true");
        }
      } else {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem("access", token);
          sessionStorage.setItem(REMEMBER_KEY, "false");
        }
      }
    } catch {}
  },
  clear() {
    if (typeof sessionStorage !== "undefined") {
      for (const k of ACCESS_KEYS) sessionStorage.removeItem(k);
      sessionStorage.removeItem(REMEMBER_KEY);
    }
    if (typeof localStorage !== "undefined") {
      for (const k of ACCESS_KEYS) localStorage.removeItem(k);
      localStorage.removeItem(REMEMBER_KEY);
    }
  },
};

const userStore = {
  get() {
    let raw = null;
    if (typeof sessionStorage !== "undefined") {
      for (const k of USER_KEYS) { raw ||= sessionStorage.getItem(k); if (raw) break; }
    }
    if (!raw && typeof localStorage !== "undefined") {
      for (const k of USER_KEYS) { raw ||= localStorage.getItem(k); if (raw) break; }
    }
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },
  set(user, remember = false) {
    if (!user) return this.clear();
    const json = JSON.stringify(user);
    try {
      if (typeof sessionStorage !== "undefined") for (const k of USER_KEYS) sessionStorage.removeItem(k);
      if (typeof localStorage !== "undefined")  for (const k of USER_KEYS) localStorage.removeItem(k);
    } catch {}
    if (remember && typeof localStorage !== "undefined") {
      localStorage.setItem("user", json);
    } else if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("user", json);
    }
  },
  clear() {
    if (typeof sessionStorage !== "undefined") {
      for (const k of USER_KEYS) sessionStorage.removeItem(k);
    }
    if (typeof localStorage !== "undefined") {
      for (const k of USER_KEYS) localStorage.removeItem(k);
    }
  },
};

export const getAccessToken = () => storage.get();
export const getStoredUser = () => userStore.get();

// --- utils ---
function normalizePath(endpoint) {
  if (!endpoint) return "/";
  return endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
}
function assertNoMixedContent() {
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    API_URL.startsWith("http://")
  ) {
    throw new Error(`Mixed content: app en HTTPS pero API_URL=${API_URL}. Usa HTTPS en VITE_API_URL.`);
  }
}
// API_URL ya termina en /api. Evita duplicar si alguien pasa "/api/...."
function join(path = "") {
  const BASE = API_URL.replace(/\/+$/, "");
  let p = normalizePath(path);
  if (BASE.endsWith("/api") && p.startsWith("/api/")) p = p.slice(4);
  return BASE + p;
}

function willSendBody(method, body) {
  if (method === "GET" || method === "HEAD") return false;
  return body !== undefined && body !== null;
}

function buildHeaders(extraHeaders = {}, token, method, body) {
  const headers = { ...extraHeaders };
  const t = token ?? storage.get();
  if (t) headers.Authorization = `Bearer ${t}`;
  const isFormData = (typeof FormData !== "undefined") && body instanceof FormData;
  if (willSendBody(method, body) && !headers["Content-Type"] && !isFormData) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

function base64UrlDecode(str) {
  try {
    const pad = "=".repeat((4 - (str.length % 4)) % 4);
    const b64 = (str.replace(/-/g, "+").replace(/_/g, "/") + pad);
    const decoded = typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
    const bytes = Uint8Array.from(decoded, c => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}
function parseJwt(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = base64UrlDecode(parts[1]);
    return json ? JSON.parse(json) : null;
  } catch { return null; }
}
function roleFromToken(t) {
  return parseJwt(t)?.role ?? null;
}

export function getRole() {
  return getStoredUser()?.role ?? roleFromToken(getAccessToken());
}
export function isAdmin() {
  return (getRole() || "").toLowerCase() === "admin";
}
export function getAuthSnapshot() {
  const token = getAccessToken();
  const user = getStoredUser();
  const role = user?.role ?? roleFromToken(token);
  return { token, user, role };
}

async function safeErrorText(res) {
  try {
    const j = await res.json();
    const detail = typeof j?.detail === "string"
      ? j.detail
      : (Array.isArray(j?.detail) ? j.detail.map(x => x?.msg || "").join("; ") : null);
    return detail || j?.message || JSON.stringify(j);
  } catch {
    try { return await res.text(); } catch { return null; }
  }
}

async function parseBody(res) {
  if (res.status === 204) return null;
  const ct = (res.headers.get("content-type") || "").toLowerCase();

  if (ct.includes("application/json")) {
    try { return await res.json(); } catch { return null; }
  }

  if (ct.startsWith("text/") || ct.includes("application/problem+json")) {
    try {
      const txt = await res.text();
      try { return txt ? JSON.parse(txt) : null; } catch { return txt; }
    } catch { return null; }
  }

  return null;
}

// ====== refresh concurrente (cola) ======
let refreshing = false;
let waiters = [];

// Preserva modo remember al refrescar
async function refreshAccess() {
  const url = join("/auth/refresh");
  const opts = { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } };
  const res = await fetch(url, opts);
  if (!res.ok) {
    storage.clear();
    userStore.clear();
    throw new Error("No se pudo refrescar la sesión");
  }
  const data = await res.json();
  const newAccess = data?.access_token;
  if (!newAccess) throw new Error("Refresh sin access_token");

  const keepRemember = storage.remember();
  storage.set(newAccess, keepRemember);

  if (data?.user) {
    userStore.set(data.user, keepRemember);
  } else {
    const role = roleFromToken(newAccess);
    const current = userStore.get() || {};
    if (role && current?.role !== role) userStore.set({ ...current, role }, keepRemember);
  }

  return newAccess;
}

// ====== core request (JSON/text) ======
async function request(
  method,
  endpoint,
  { body, token = null, headers = {}, signal, retry = false, withCredentials = USE_CREDENTIALS } = {}
) {
  assertNoMixedContent();

  const url = join(endpoint);
  const isFormData = (typeof FormData !== "undefined") && body instanceof FormData;

  const opts = {
    method,
    signal,
    headers: buildHeaders(headers, token, method, body),
    credentials: withCredentials ? "include" : "same-origin",
  };

  if (willSendBody(method, body)) {
    opts.body = isFormData ? body : (typeof body === "string" ? body : JSON.stringify(body));
  }

  const res = await fetch(url, opts);

  const hasToken = !!(token ?? storage.get());
  const isAuthEndpoint = /^\/auth\//.test(normalizePath(endpoint));

  if (res.status === 401 && !retry) {
    if (!hasToken || isAuthEndpoint) {
      const errText = await safeErrorText(res);
      throw new Error(errText || "Credenciales inválidas");
    }

    if (!refreshing) {
      refreshing = true;
      try {
        await refreshAccess();
        waiters.forEach((resume) => resume());
      } catch (err) {
        waiters.forEach((resume) => resume());
        storage.clear();
        userStore.clear();
        if (typeof window !== "undefined") window.location.href = "/login";
        throw err;
      } finally {
        refreshing = false;
        waiters = [];
      }
    } else {
      await new Promise((resolve) => waiters.push(resolve));
    }
    const retried = await fetch(url, {
      ...opts,
      headers: buildHeaders(headers, storage.get(), method, body),
    });
    if (!retried.ok) {
      if (retried.status === 401) {
        storage.clear();
        userStore.clear();
        if (typeof window !== "undefined") window.location.href = "/login";
      }
      const errText = await safeErrorText(retried);
      throw new Error(errText || `Error ${retried.status} en ${endpoint}`);
    }
    return parseBody(retried);
  }

  if (!res.ok) {
    const errText = await safeErrorText(res);
    throw new Error(errText || `Error ${res.status} en ${endpoint}`);
  }
  return parseBody(res);
}

// ====== core download (BLOb) ======
async function downloadRaw(
  method,
  endpoint,
  { body = null, token = null, headers = {}, signal, withCredentials = USE_CREDENTIALS } = {}
) {
  assertNoMixedContent();

  const url = join(endpoint);
  const isFormData = (typeof FormData !== "undefined") && body instanceof FormData;

  const makeOpts = (tk) => ({
    method,
    signal,
    credentials: withCredentials ? "include" : "same-origin",
    headers: buildHeaders(headers, tk, method, body),
    body: willSendBody(method, body)
      ? (isFormData ? body : (typeof body === "string" ? body : JSON.stringify(body)))
      : undefined,
  });

  // primer intento
  let res = await fetch(url, makeOpts(token));

  // si 401 -> refresca y reintenta una vez
  if (res.status === 401) {
    try {
      await refreshAccess();
      res = await fetch(url, makeOpts(storage.get()));
    } catch (e) {
      return res;
    }
  }
  return res;
}

// ===== API de alto nivel =====
export const api = {
  get(endpoint, token = null, extraHeaders = {}, signal) {
    return request("GET", endpoint, { token, headers: extraHeaders, signal });
  },
  post(endpoint, body, token = null, extraHeaders = {}, signal) {
    return request("POST", endpoint, { body, token, headers: extraHeaders, signal });
  },
  put(endpoint, body, token = null, extraHeaders = {}, signal) {
    return request("PUT", endpoint, { body, token, headers: extraHeaders, signal });
  },
  patch(endpoint, body, token = null, extraHeaders = {}, signal) {
    return request("PATCH", endpoint, { body, token, headers: extraHeaders, signal });
  },
  // admite opts extra: { withCredentials: boolean }
  delete(endpoint, token = null, extraHeaders = {}, signal, opts = {}) {
    return request("DELETE", endpoint, { token, headers: extraHeaders, signal, ...opts });
  },
  postForm(endpoint, formData, token = null, extraHeaders = {}, signal) {
    return request("POST", endpoint, { body: formData, token, headers: extraHeaders, signal });
  },

  // ⚡ Descargas/exports (devuelve Response)
  async download(endpoint, method = "GET", body = null, token = null, extraHeaders = {}, signal) {
    const res = await downloadRaw(method, endpoint, { body, token, headers: extraHeaders, signal });
    if (!res.ok) {
      const msg = await safeErrorText(res);
      throw new Error(msg || `Error ${res.status} en descarga`);
    }
    return res; // el caller hace res.blob()
  },
};

// ===== Helpers de Auth =====
export async function doLogin(email, password, remember = false) {
  const data = await api.post("/auth/login-json", { email, password });
  const access = data?.access_token;
  if (!access) throw new Error("Login sin access_token");

  storage.set(access, remember);

  // Si el backend devuelve el user completo, lo guardamos tal cual
  if (data?.user) {
    userStore.set(data.user, remember);
  } else {
    // Si no, al menos rol del token
    const role = roleFromToken(access);
    userStore.set({ email, role }, remember);

    // ⬇️ NUEVO: intenta hidratar con /me para obtener brand_dir, company_name, etc.
    try {
      const me = await getMe(access);
      if (me?.user) userStore.set(me.user, remember);
    } catch {
      // si falla, nos quedamos con la info mínima
    }
  }

  return data;
}

export async function doLogout() {
  try { await api.post("/auth/logout"); } catch {}
  storage.clear();
  userStore.clear();
  if (typeof window !== "undefined") window.location.href = "/login";
}

// ===== Helpers /me =====

// Intenta varios endpoints de perfil con el mismo token, devuelve { user } si existe.
export async function getMe(token = null) {
  const tk = token ?? storage.get();
  const headers = tk ? { Authorization: `Bearer ${tk}` } : {};
  const optsWithCreds = tk ? { headers } : { credentials: "include" };

  // Intentamos rutas típicas en tu app/infra
  const candidates = ["/me", "/auth/me", "/api/me", "/api/auth/me"];

  for (const ep of candidates) {
    try {
      const res = await fetch(join(ep), {
        method: "GET",
        ...optsWithCreds,
      });
      if (!res.ok) continue;
      const json = await parseBody(res);
      if (json?.user) return json;
    } catch {
      // probar siguiente
    }
  }
  return null;
}

// Hidrata y guarda el user real (brand_dir, company_name, …) preservando remember
export async function hydrateUserFromMe() {
  const tk = storage.get();
  if (!tk) return null;
  const me = await getMe(tk);
  if (me?.user) {
    const keepRemember = storage.remember();
    userStore.set(me.user, keepRemember);
    return me.user;
  }
  return null;
}

// ===== Helpers de Calendario =====
export function fetchUserCalendarEvents(userId, start, end, token = null, signal) {
  const qs = new URLSearchParams({ start, end }).toString();
  const hasUserId = userId !== null && userId !== undefined && String(userId).trim() !== "";
  const basePath = hasUserId ? `/calendar/users/${encodeURIComponent(userId)}/events` : "/calendar/events";
  return api.get(`${basePath}?${qs}`, token, {}, signal).then((r) => {
    const arr = r?.events || [];
    return arr.map(ev => ({
      ...ev,
      type: ev.tipo,
      fecha: ev.date,
      titulo: ev.title,
    }));
  });
}

export function fetchWorkingDays(userId, start, end, token = null, signal) {
  const qs = new URLSearchParams({ start, end }).toString();
  return api.get(`/calendar/users/${userId}/working-days?${qs}`, token, {}, signal);
}
export function fetchWorkingDaysMe(start, end, token = null, signal) {
  const qs = new URLSearchParams({ start, end }).toString();
  return api.get(`/calendar/working-days?${qs}`, token, {}, signal);
}
export function fetchUserWorkingDays(...args) {
  return fetchWorkingDays(...args);
}

export function setAccessToken(token, remember = false) {
  if (!token) {
    storage.clear();
    return;
  }
  storage.set(token, remember);
}
export function setStoredUser(user, remember = false) {
  if (!user) {
    userStore.clear();
    return;
  }
  userStore.set(user, remember);
}

// ===== Helpers de Exportación =====
export async function exportLogsGET(formato = "csv") {
  // GET /logs/export?formato=...
  const res = await api.download(`/logs/export?formato=${encodeURIComponent(formato)}`, "GET");
  return res.blob();
}

export async function exportLogsPOST(datos, formato = "csv") {
  // POST /logs/exportar_logs?formato=...
  const res = await api.download(
    `/logs/exportar_logs?formato=${encodeURIComponent(formato)}`,
    "POST",
    datos
  );
  return res.blob();
}
